import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, CreditCard, Ticket, BarChart2, PieChart, TrendingUp, AlertCircle } from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell, Legend
} from 'recharts';
import { getDashboardStats } from '../../services/adminService';

const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      setLoading(true);
      try {
        const response = await getDashboardStats();
        if (response.success) {
          setStats(response.data);
        } else {
          setError(response.error || 'Failed to fetch dashboard statistics');
        }
      } catch (err) {
        setError('An error occurred while fetching dashboard data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  // Use mock data if API call fails
  const dashboardData = stats || {
    userCounts: {
      candidates: 245,
      employers: 78,
      total: 323
    },
    financials: {
      totalRevenue: 1250000,
      monthlyRevenue: [
        { month: '2025-04', amount: 180000 },
        { month: '2025-05', amount: 195000 },
        { month: '2025-06', amount: 210000 },
        { month: '2025-07', amount: 225000 },
        { month: '2025-08', amount: 240000 },
        { month: '2025-09', amount: 200000 }
      ],
      activePlans: 65
    },
    tickets: {
      counts: {
        open: 12,
        'in-progress': 8,
        resolved: 25,
        closed: 42
      },
      total: 87
    },
    planDistribution: {
      starter: 25,
      basic: 30,
      pro: 15,
      enterprise: 8
    },
    recentTickets: [
      {
        id: 'T-1001',
        title: 'Cannot access candidate profiles',
        status: 'open',
        priority: 'high',
        employer: { name: 'TechCorp Inc.' },
        createdAt: '2025-09-25T10:30:00Z'
      },
      {
        id: 'T-1002',
        title: 'Billing issue with subscription',
        status: 'in-progress',
        priority: 'medium',
        employer: { name: 'Global Solutions Ltd.' },
        createdAt: '2025-09-24T14:15:00Z'
      }
    ],
    recentPayments: [
      {
        id: 'P-2001',
        amount: 7999,
        status: 'completed',
        employer: { name: 'TechCorp Inc.' },
        planId: 'pro',
        createdAt: '2025-09-28T09:15:00Z'
      },
      {
        id: 'P-2002',
        amount: 19999,
        status: 'completed',
        employer: { name: 'Global Solutions Ltd.' },
        planId: 'enterprise',
        createdAt: '2025-09-27T11:30:00Z'
      }
    ]
  };

  // Prepare data for charts with safe navigation
  const revenueChartData = (dashboardData?.financials?.monthlyRevenue || []).map(item => ({
    month: new Date(item.month).toLocaleString('default', { month: 'short' }),
    amount: item.amount / 1000 // Convert to thousands for better display
  }));

  const ticketChartData = Object.entries(dashboardData?.tickets?.counts || {}).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value
  }));

  const planChartData = Object.entries(dashboardData?.planDistribution || {}).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value
  }));

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Total Users Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm text-gray-500 mb-1">Total Users</p>
              <h3 className="text-3xl font-bold text-gray-900">{dashboardData?.userCounts?.total || 0}</h3>
            </div>
            <div className="p-2 bg-blue-50 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="flex items-center text-xs">
            <span className="text-green-600 font-semibold">↑ 8%</span>
            <span className="text-gray-500 ml-1">vs last month</span>
          </div>
        </motion.div>

        {/* New Customers Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm text-gray-500 mb-1">New Employers</p>
              <h3 className="text-3xl font-bold text-gray-900">{dashboardData?.userCounts?.employers || 0}</h3>
            </div>
            <div className="p-2 bg-orange-50 rounded-lg">
              <Users className="h-6 w-6 text-orange-600" />
            </div>
          </div>
          <div className="flex items-center text-xs">
            <span className="text-red-600 font-semibold">↓ 7%</span>
            <span className="text-gray-500 ml-1">vs last month</span>
          </div>
        </motion.div>

        {/* Open Tickets Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm text-gray-500 mb-1">Open Tickets</p>
              <h3 className="text-3xl font-bold text-gray-900">{dashboardData?.tickets?.counts?.open || 0}</h3>
            </div>
            <div className="p-2 bg-red-50 rounded-lg">
              <Ticket className="h-6 w-6 text-red-600" />
            </div>
          </div>
          <div className="flex items-center text-xs">
            <span className="text-red-600 font-semibold">↑ 4.8%</span>
            <span className="text-gray-500 ml-1">vs last month</span>
          </div>
        </motion.div>

        {/* Total Revenue Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm text-gray-500 mb-1">Total Revenue</p>
              <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(dashboardData?.financials?.totalRevenue || 0)}</h3>
            </div>
            <div className="p-2 bg-green-50 rounded-lg">
              <CreditCard className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="flex items-center text-xs">
            <span className="text-gray-500">Last month: </span>
            <span className="text-gray-700 font-semibold ml-1">{formatCurrency(dashboardData?.financials?.monthlyRevenue?.[(dashboardData?.financials?.monthlyRevenue?.length || 1) - 1]?.amount || 0)}</span>
          </div>
        </motion.div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Revenue Analytics Chart - Takes 2 columns */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold text-gray-900">Revenue Analytics</h3>
            <select className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-500">
              <option>This Week</option>
              <option>This Month</option>
              <option>This Year</option>
            </select>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis 
                  dataKey="month" 
                  stroke="#9CA3AF" 
                  style={{ fontSize: '11px' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis 
                  tickFormatter={(value) => `${value}k`} 
                  stroke="#9CA3AF" 
                  style={{ fontSize: '11px' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip 
                  formatter={(value) => [`₹${value}K`, 'Revenue']}
                  contentStyle={{ 
                    backgroundColor: '#FFF', 
                    border: '1px solid #E5E7EB', 
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  cursor={{ fill: 'rgba(249, 115, 22, 0.1)' }}
                />
                <Bar dataKey="amount" fill="#F97316" radius={[6, 6, 0, 0]} barSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Total Income Chart - Takes 1 column */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Total Income</h3>
          <p className="text-xs text-gray-500 mb-5">View your total income in a certain period of time</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                <XAxis type="number" stroke="#9CA3AF" style={{ fontSize: '11px' }} axisLine={false} tickLine={false} />
                <YAxis 
                  type="category" 
                  dataKey="month" 
                  stroke="#9CA3AF" 
                  style={{ fontSize: '11px' }}
                  axisLine={false}
                  tickLine={false}
                  width={35}
                />
                <Tooltip 
                  formatter={(value) => [`₹${value}K`, 'Income']}
                  contentStyle={{ 
                    backgroundColor: '#FFF', 
                    border: '1px solid #E5E7EB', 
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Bar dataKey="amount" stackId="a" fill="#DC2626" radius={[0, 4, 4, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Recent Orders Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100"
      >
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Recent Tickets</h3>
            <div className="flex items-center gap-3">
              <div className="relative">
                <input 
                  type="text" 
                  placeholder="Search" 
                  className="text-sm border border-gray-200 rounded-lg pl-9 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 w-64"
                />
                <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <button className="text-sm border border-gray-200 rounded-lg px-4 py-2 hover:bg-gray-50 transition-colors">
                Sort by ↓
              </button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {(dashboardData?.recentTickets || []).map((ticket) => (
                <tr key={ticket.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">#{ticket.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDate(ticket.createdAt)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{ticket.employer?.name || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{ticket.category || 'General'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2.5 py-1 inline-flex text-xs font-medium rounded-md
                      ${ticket.status === 'open' ? 'bg-orange-100 text-orange-700' : 
                        ticket.status === 'in-progress' ? 'bg-yellow-100 text-yellow-700' : 
                        'bg-green-100 text-green-700'}`}>
                      {ticket.status === 'open' ? 'Pending' : ticket.status === 'in-progress' ? 'In Progress' : 'Completed'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{ticket.priority || 'Medium'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default DashboardOverview;
